import { useTranslation } from 'react-i18next';
import { useWheelContext } from 'pages/DownloadTeamWheel/hooks/useWheelContext';
import { useTheme } from '@mui/material';
import { calculateStringPixelLength } from 'pages/DownloadTeamWheel/TeamTable/TeamTable';
import {
  BASE_TABLE_WIDTH,
  type FormattedHeader,
  splitText,
  WIDTH_BUFFER_RATIO,
} from 'pages/DownloadTeamWheel/TeamTable/teamTable.helper';
import getTableXCoordinates, { type Column } from './getTableXCoordinates';

type TeamTableHeaderProps = {
  largestNameWidth: number;
  showNumbers: boolean;
  showOrganisations: boolean;
  isMultiOrg: boolean;
};

const TeamTableHeader = ({
  largestNameWidth,
  showNumbers = true,
  showOrganisations = true,
  isMultiOrg = false,
}: TeamTableHeaderProps) => {
  const theme = useTheme();
  const [{ tableFontSize }] = useWheelContext();
  const { t } = useTranslation();

  const { columns, lengthRemoved, columnLengths } = getTableXCoordinates(
    showNumbers,
    largestNameWidth,
    isMultiOrg && showOrganisations,
  );

  const minWidth = BASE_TABLE_WIDTH - lengthRemoved;

  const headerConfig: {
    key: Column;
    label: string;
    visible: boolean;
  }[] = [
    {
      key: 'numbers',
      label: isMultiOrg
        ? t('ui.event-management.events.team-wheel.table.number-abbrev')
        : t('ui.event-management.events.team-wheel.table.number'),
      visible: showNumbers,
    },
    {
      key: 'initials',
      label: 'ui.event-management.events.team-wheel.table.initials',
      visible: true,
    },
    {
      key: 'name',
      label: 'ui.event-management.events.team-wheel.table.name',
      visible: true,
    },
    {
      key: 'position',
      label: 'ui.event-management.events.team-wheel.table.wheel-position',
      visible: true,
    },
    {
      key: 'organisation',
      label: 'ui.event-management.events.team-wheel.table.organisation',
      visible: showOrganisations && isMultiOrg,
    },
    {
      key: 'colourEnergies',
      label: 'ui.event-management.events.team-wheel.table.order-preference',
      visible: true,
    },
  ];
  const visibleColumns = headerConfig.filter((column) => column.visible);

  const splitAndTruncateHeader = (
    text: string,
    maxWidth: number,
    fontSize: number,
  ): FormattedHeader => {
    const font = `${fontSize}px ${theme.typography.fontFamily}`;

    const totalWidth = calculateStringPixelLength(text, font);
    const avgCharWidth = (totalWidth * WIDTH_BUFFER_RATIO) / text.length;
    const maxChars = Math.floor(maxWidth / avgCharWidth);

    if (text.length <= maxChars) {
      return { line1: text };
    }

    return splitText(text, maxChars);
  };

  return (
    <g>
      <rect
        fill={theme.palette.blue.main}
        width={minWidth + largestNameWidth}
        height={15}
      />

      {visibleColumns.map((col) => {
        const x = columns[col.key] ?? 0;
        const label = t(col.label);
        const maxWidth = columnLengths[col.key];
        const { line1, line2 } = splitAndTruncateHeader(
          label,
          maxWidth,
          tableFontSize,
        );

        return (
          <text
            key={col.key}
            y={5}
            dominantBaseline="middle"
            fontSize={tableFontSize}
            fontWeight="bold"
            fill={theme.palette.grey[100]}
            role="row"
          >
            <tspan role="columnheader" x={x} dy={0}>
              {line1}
            </tspan>

            {line2 && (
              <tspan role="columnheader" x={x} dy="1em">
                {line2}
              </tspan>
            )}
          </text>
        );
      })}
    </g>
  );
};
export default TeamTableHeader;
